import React from 'react';
import { Flex } from 'glints-aries/es';
import { Badge, Divider, Icon, Typography } from 'glints-aries/es/@next';
import { IconNames } from 'glints-aries/es/@next/Icon/icons/icons';
import { Green, Neutral } from 'glints-aries/es/@next/utilities/colors';
import { space4 } from 'glints-aries/es/@next/utilities/spacing';
import { compact } from 'lodash-es';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';

import {
  ID_COUNTRY_LOCATION,
  MY_COUNTRY_LOCATION,
  SG_COUNTRY_LOCATION,
} from '../../../common/constants';
import { useGetCompanyFreeTrialOffer } from '../../../common/hooks/useGetCompanyFreeTrialOffer';
import { useGetSessionCompany } from '../../../common/hooks/useGetSessionCompany';
import {
  EmployerPurchaseProductType,
  VerificationMethod,
} from '../../../common/interfaces/entities';
import { CompanyProductFreeTrialOffer } from '../../../common/interfaces/product-free-trial-offer';
import { timePeriodUnitToText } from '../../../modules/CreditSystem/constants';
import { useIsViewOnlyMode } from './FormContext';
import { VerificationBenefitOutput } from './hooks/useGetCompanyVerificationMethods';
import { VerificationMethodCardStyles } from './styles.sc';

const VerificationMethodToComponentMap = {
  [VerificationMethod.LEGAL_DOCUMENTS]: (
    <FormattedMessage
      id="text-verify-with-legal-documents"
      defaultMessage="Verify with Legal Documents"
    />
  ),
  [VerificationMethod.PROOF_OF_OWNERSHIP]: (
    <FormattedMessage
      id="text-verify-with-proof-of"
      defaultMessage="Verify with Proof of Ownership"
    />
  ),
};
const VerificationMethodToHelperText: Record<
  VerificationMethod,
  Record<string, React.ReactNode> & { default: React.ReactNode }
> = {
  [VerificationMethod.LEGAL_DOCUMENTS]: {
    [ID_COUNTRY_LOCATION.code]: (
      <FormattedMessage
        id="text-for-businesses-with-legal-entities"
        defaultMessage="For businesses with legal entities such as PT and CV"
      />
    ),
    [SG_COUNTRY_LOCATION.code]: (
      <FormattedMessage
        id="text-legal-company-desc-sg"
        defaultMessage="For businesses which are registered with ACRA. Requires official documents such as ACRA Bizfile and other supporting documents."
      />
    ),
    [MY_COUNTRY_LOCATION.code]: (
      <FormattedMessage
        id="text-legal-company-desc-my"
        defaultMessage="For businesses registered in Malaysia. Requires official documents such as SSM corporate profile."
      />
    ),
    default: (
      <FormattedMessage
        id="text-legal-company-desc-default"
        defaultMessage="For registered businesses in your country. Please provide official company registration documents issued by the relevant authority."
      />
    ),
  },
  [VerificationMethod.PROOF_OF_OWNERSHIP]: {
    default: (
      <FormattedMessage
        id="for-individual-or-sole-prorietorship"
        defaultMessage="For individual or sole proprietorship businesses"
      />
    ),
  },
};
const VerificationMethodToIconMap: Record<VerificationMethod, IconNames> = {
  [VerificationMethod.LEGAL_DOCUMENTS]: 'ri-legal-document-line',
  [VerificationMethod.PROOF_OF_OWNERSHIP]: 'ri-folder-user2-line',
};

const BenefitsComponents = {
  ActiveJobs: (props: { activeJobs: number }) => (
    <Flex flexDirection="column" alignItems="center">
      <Typography variant="headline6" color={Neutral.B40}>
        {props.activeJobs}
      </Typography>
      <Typography variant="subtitle2" color={Neutral.B40}>
        <FormattedMessage
          id="text-active-jobs-table"
          defaultMessage="Active Jobs"
        />
      </Typography>
    </Flex>
  ),
  VerifiedBadge: () => (
    <Flex flexDirection="column" alignItems="center" gap="4px">
      <Icon name="ri-shield-check" fill={Green.B52} width={24} height={24} />
      <Typography variant="subtitle2" color={Neutral.B40}>
        <FormattedMessage
          id="text-verified-badge"
          defaultMessage="Verified Badge"
        />
      </Typography>
    </Flex>
  ),
  PaidFeatures: () => (
    <Flex flexDirection="column" alignItems="center" gap="4px">
      <Icon
        name="ri-shopping-cart-line"
        fill={Neutral.B40}
        width={24}
        height={24}
      />
      <Typography variant="subtitle2" color={Neutral.B40}>
        <FormattedMessage
          id="text-paid-features"
          defaultMessage="Paid features"
        />
      </Typography>
    </Flex>
  ),
  VIPFreeTrial: ({ vipOffer }: { vipOffer: CompanyProductFreeTrialOffer }) => {
    const { formatMessage } = useIntl();
    return (
      <Flex flexDirection="column" alignItems="center" gap="4px">
        <img
          src="images/credit-system/vip-free-trial/vip-free-trial-icon.svg"
          width={24}
        />
        {vipOffer.productTimePeriodUnit && (
          <Typography variant="subtitle2" color={Neutral.B40}>
            <FormattedMessage
              id="text-duration-vip-trial"
              defaultMessage="{duration} {timeUnit} VIP Trial"
              values={{
                duration: vipOffer.productTimePeriod,
                timeUnit: formatMessage(
                  timePeriodUnitToText[vipOffer.productTimePeriodUnit],
                  {
                    count: vipOffer.productTimePeriod,
                  }
                ),
              }}
            />
          </Typography>
        )}
      </Flex>
    );
  },
};
export function VerificationMethodCard({
  benefits,
  verificationMethod,
  popularityTag,
  active,
  onClick,
  hidePopularityTag = false,
  disabled = false,
}: {
  benefits: VerificationBenefitOutput;
  verificationMethod: VerificationMethod;
  popularityTag: MessageDescriptor | null;
  active: boolean;
  onClick: (() => void) | undefined;
  hidePopularityTag?: boolean;
  disabled?: boolean;
}) {
  const { sessionCompany } = useGetSessionCompany();
  const vipOffer = useGetCompanyFreeTrialOffer({
    productType: EmployerPurchaseProductType.VIP_MEMBERSHIP,
    options: {
      fetchPolicy: 'cache-first',
    },
  });

  const companyCountryCode = sessionCompany?.CountryCode;

  const title = VerificationMethodToComponentMap[verificationMethod];
  const helperText =
    (companyCountryCode &&
      VerificationMethodToHelperText[verificationMethod][companyCountryCode]) ??
    VerificationMethodToHelperText[verificationMethod].default;
  const iconName = VerificationMethodToIconMap[verificationMethod];
  const benefitsComponents = compact([
    benefits.verifiedBadge && <BenefitsComponents.VerifiedBadge />,
    benefits.activeJobCount > 0 && (
      <BenefitsComponents.ActiveJobs activeJobs={benefits.activeJobCount} />
    ),
    benefits.paidFeatures && <BenefitsComponents.PaidFeatures />,
    vipOffer && <BenefitsComponents.VIPFreeTrial vipOffer={vipOffer} />,
  ]);
  const isViewOnly = useIsViewOnlyMode();

  return (
    <VerificationMethodCardStyles.Container
      data-active={active}
      data-view-only={isViewOnly}
      data-disabled={disabled}
      onClick={onClick}
    >
      <VerificationMethodCardStyles.TagContainer>
        {popularityTag && !isViewOnly && !hidePopularityTag && (
          <Badge status="primary">
            <Typography variant="overline" color={Neutral.B100}>
              <FormattedMessage {...popularityTag} />
            </Typography>
          </Badge>
        )}
      </VerificationMethodCardStyles.TagContainer>
      <Flex
        gap="20px"
        alignItems="center"
        justifyContent="space-between"
        style={isViewOnly ? { flex: 1 } : undefined}
      >
        <Flex flexDirection="column" gap={space4} justifyContent="flex-start">
          <Typography variant="body2" color={Neutral.B18}>
            {title}
          </Typography>
          <Typography variant="subtitle2" color={Neutral.B40}>
            {helperText}
          </Typography>
        </Flex>
        <VerificationMethodCardStyles.Icon
          data-active={active}
          data-view-only={isViewOnly}
          name={iconName}
        />
      </Flex>
      <VerificationMethodCardStyles.DividerContainer
        data-view-only={isViewOnly}
      >
        <Divider />
        <Typography
          variant="overline"
          color={Neutral.B40}
          style={{ textTransform: 'uppercase', whiteSpace: 'nowrap' }}
        >
          <FormattedMessage
            id="text-unlocked-benefits"
            defaultMessage="Unlocked benefits"
          />
        </Typography>
        <Divider />
      </VerificationMethodCardStyles.DividerContainer>
      <VerificationMethodCardStyles.BenefitsContainer>
        {benefitsComponents}
      </VerificationMethodCardStyles.BenefitsContainer>
    </VerificationMethodCardStyles.Container>
  );
}
